"use client";

import { useRef, useState, useTransition } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  adminRegisterAction,
  loginAction,
  verifySuperClaveAction,
} from "@/actions/auth";
import { PasswordInput } from "@/components/auth/PasswordInput";

type View = "login" | "register";

export function AuthForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [view, setView] = useState<View>("login");
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const [superClaveOpen, setSuperClaveOpen] = useState(false);
  const [superClave, setSuperClave] = useState("");
  const [superClaveError, setSuperClaveError] = useState<string | null>(null);
  const [verifiedSuperClave, setVerifiedSuperClave] = useState<string | null>(
    null
  );

  const [askAnotherOpen, setAskAnotherOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  function resetRegisterForm() {
    formRef.current?.reset();
    setError(null);
  }

  function handleVerifySuperClave(e: React.FormEvent) {
    e.preventDefault();
    setSuperClaveError(null);

    startTransition(async () => {
      const result = await verifySuperClaveAction(superClave);
      if (!result.success) {
        setSuperClaveError(result.error);
        return;
      }

      setVerifiedSuperClave(superClave);
      setSuperClave("");
      setSuperClaveOpen(false);
      setError(null);
      setView("register");
    });
  }

  function handleLoginSubmit(formData: FormData) {
    setError(null);
    startTransition(async () => {
      const result = await loginAction(formData);
      if (result && !result.success) {
        setError(result.error);
      }
    });
  }

  function handleRegisterSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!verifiedSuperClave) {
      setError("Debes validar la Super Clave primero");
      return;
    }

    const formData = new FormData(e.currentTarget);
    setError(null);

    startTransition(async () => {
      const result = await adminRegisterAction({
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? ""),
        password: String(formData.get("password") ?? ""),
        superClave: verifiedSuperClave,
      });

      if (!result.success) {
        setError(result.error);
        return;
      }

      setRegisteredEmail(result.data.email);
      setAskAnotherOpen(true);
    });
  }

  function handleRegisterAnother(yes: boolean) {
    setAskAnotherOpen(false);
    setRegisteredEmail(null);

    if (yes) {
      resetRegisterForm();
      setView("register");
      return;
    }

    resetRegisterForm();
    setVerifiedSuperClave(null);
    setView("login");
  }

  const fieldClass =
    "w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--fp-teal)] focus:bg-white focus:ring-2 focus:ring-[var(--fp-cyan)]/30";

  return (
    <>
      <form
        ref={formRef}
        key={view}
        className="w-full space-y-5 rounded-2xl border border-white/15 bg-white/95 p-7 text-slate-900 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-md sm:p-8"
        action={view === "login" ? handleLoginSubmit : undefined}
        onSubmit={view === "register" ? handleRegisterSubmit : undefined}
      >
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-[var(--fp-teal)] uppercase">
            Acceso seguro
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--fp-navy)]">
            {view === "login" ? "Iniciar sesión" : "Registrar"}
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
            {view === "login"
              ? "Ingresa tus credenciales para abrir tu unidad de almacenamiento."
              : "Crea una cuenta de usuario en FonpacificoDrive."}
          </p>
        </div>

        {view === "register" && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Nombre
            </label>
            <input
              name="name"
              required
              className={fieldClass}
              placeholder="Nombre completo"
            />
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            name="email"
            type="email"
            required
            className={fieldClass}
            placeholder="tu@email.com"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Contraseña
          </label>
          <PasswordInput
            name="password"
            required
            minLength={6}
            autoComplete={
              view === "login" ? "current-password" : "new-password"
            }
            inputClassName={fieldClass + " pr-10"}
          />
        </div>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </p>
        )}

        {view === "login" ? (
          <div className="flex gap-2.5 pt-1">
            <button
              type="submit"
              disabled={pending}
              className="w-[75%] rounded-xl bg-[linear-gradient(135deg,var(--fp-teal),var(--fp-navy))] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
            >
              {pending ? "Procesando..." : "Entrar"}
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                setSuperClave("");
                setSuperClaveError(null);
                setSuperClaveOpen(true);
              }}
              className="w-[25%] rounded-xl border border-[var(--fp-teal)] bg-white px-2 py-3 text-sm font-semibold text-[var(--fp-teal)] transition hover:bg-cyan-50 disabled:opacity-50"
            >
              Registrar
            </button>
          </div>
        ) : (
          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              disabled={pending}
              onClick={() => {
                resetRegisterForm();
                setVerifiedSuperClave(null);
                setView("login");
              }}
              className="w-[25%] rounded-xl border border-slate-300 bg-white px-2 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Volver
            </button>
            <button
              type="submit"
              disabled={pending}
              className="w-[75%] rounded-xl bg-[linear-gradient(135deg,var(--fp-teal),var(--fp-navy))] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
            >
              {pending ? "Registrando..." : "Registrar"}
            </button>
          </div>
        )}
      </form>

      {superClaveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--fp-deep)]/70 p-4 backdrop-blur-sm">
          <form
            onSubmit={handleVerifySuperClave}
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-slate-900 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-[var(--fp-navy)]">
              Super Clave
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Ingresa la Super Clave para habilitar el registro de usuarios.
            </p>

            <label className="mt-4 block text-sm font-medium text-slate-700">
              Super Clave
            </label>
            <PasswordInput
              required
              value={superClave}
              onChange={setSuperClave}
              className="mt-1.5"
              placeholder="Ingresa la Super Clave"
              autoComplete="off"
              inputClassName={fieldClass + " pr-10"}
            />

            {superClaveError && (
              <p className="mt-2 text-sm text-red-600">{superClaveError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setSuperClaveOpen(false);
                  setSuperClave("");
                  setSuperClaveError(null);
                }}
                className="rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={pending || !superClave.trim()}
                className="rounded-xl bg-[linear-gradient(135deg,var(--fp-teal),var(--fp-navy))] px-4 py-2 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50"
              >
                {pending ? "Validando..." : "Continuar"}
              </button>
            </div>
          </form>
        </div>
      )}

      {askAnotherOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--fp-deep)]/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center text-slate-900 shadow-xl">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-[var(--fp-navy)]">
              Registro exitoso
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {registeredEmail
                ? `Se creó la cuenta ${registeredEmail}.`
                : "La cuenta se creó correctamente."}{" "}
              ¿Desea registrar otro?
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => handleRegisterAnother(false)}
                className="w-1/2 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                No
              </button>
              <button
                type="button"
                onClick={() => handleRegisterAnother(true)}
                className="w-1/2 rounded-xl bg-[linear-gradient(135deg,var(--fp-teal),var(--fp-navy))] px-3 py-2.5 text-sm font-semibold text-white hover:brightness-110"
              >
                Sí
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
